// Phase 5b — Public engagement letter sign page.
//
// Route: /sign/:publicId  (public — no Layout, no auth)
//
// Token is in the URL fragment (window.location.hash) so it never gets sent
// as a Referer to embedded resources and never appears in server logs.  We
// pull it out on mount, strip the fragment via history.replaceState so it
// doesn't linger in the address bar, then post it in request bodies only.

import { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import api from '../services/api'

export default function EngagementSign () {
  const { publicId } = useParams()
  const [stage, setStage] = useState('loading')  // loading / gone / not_verified / need_code / signing / done / error
  const [error, setError] = useState('')
  const [status, setStatus] = useState(null)     // GET /:publicId response
  const [bodyHtml, setBodyHtml] = useState('')
  const [sentMasked, setSentMasked] = useState('')
  const [code, setCode] = useState('')
  const [typedName, setTypedName] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [saving, setSaving] = useState(false)

  // UX #19 fix — the token must survive a page refresh.  Previously we read it
  // from window.location.hash once on mount and stripped the fragment; if the
  // signer hit F5 mid-flow they lost the token and could never sign.  Now we:
  //   1. Read from the fragment when present (fresh email click)
  //   2. Immediately persist to sessionStorage keyed by publicId (tab-scoped
  //      only — clears when the tab closes)
  //   3. Strip the fragment so the token doesn't sit in the address bar
  //   4. On refresh, fall back to the sessionStorage copy
  // The sessionStorage copy is cleared on successful sign or explicit clear.
  const tokenStorageKey = `engagement_sign_token:${publicId}`
  const token = useMemo(() => {
    const raw = window.location.hash || ''
    const m = raw.match(/^#(?:t=)?([A-Za-z0-9_\-.=]+)$/)
    if (m) {
      try { sessionStorage.setItem(tokenStorageKey, m[1]) } catch {}
      return m[1]
    }
    try { return sessionStorage.getItem(tokenStorageKey) || '' } catch { return '' }
  }, [tokenStorageKey])

  useEffect(() => {
    // Strip fragment so token doesn't sit in the address bar / history.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [])

  useEffect(() => { document.title = 'Sign Engagement Letter' }, [])

  useEffect(() => {
    let cancelled = false
    async function boot () {
      try {
        const s = await api.get(`/api/public/engagement/${publicId}`)
        if (cancelled) return
        setStatus(s.data)
        if (s.data.is_voided || s.data.is_expired) { setStage('gone'); return }
        if (s.data.is_signed) { setStage('done'); return }
        if (!token) { setStage('not_verified'); setError('This link is missing its verification code.  Please use the exact URL from the email — the part after # is required.'); return }

        // Verify token → fetch body
        const v = await api.post(`/api/public/engagement/${publicId}/verify`, { token })
        if (cancelled) return
        setBodyHtml(v.data.body_html || '')
        setSentMasked(v.data.sent_to_masked || '')
        setStage('need_code')
      } catch (err) {
        if (cancelled) return
        setStage('error')
        setError(err.response?.data?.error || 'This link is invalid or expired.')
      }
    }
    boot()
    return () => { cancelled = true }
  }, [publicId, token])

  // UX #20 fix — allow "resend code" after the first send (previously the
  // button disabled forever, so a signer whose code expired mid-flow was
  // stranded).  We show a 30-second cooldown after each send to prevent
  // click-spam but let the user request a fresh code afterward.
  const [codeCooldownEnds, setCodeCooldownEnds] = useState(0)
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (codeCooldownEnds <= now) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [codeCooldownEnds, now])
  const cooldownSecondsLeft = Math.max(0, Math.ceil((codeCooldownEnds - now) / 1000))

  const requestCode = async () => {
    setError('')
    try {
      await api.post(`/api/public/engagement/${publicId}/request-code`, { token })
      setCodeSent(true)
      setCode('')   // clear stale code entry
      setCodeCooldownEnds(Date.now() + 30_000)   // 30-sec cooldown
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send code.')
    }
  }

  const sign = async (e) => {
    e.preventDefault()
    setError(''); setSaving(true)
    try {
      await api.post(`/api/public/engagement/${publicId}/sign`, {
        token, code: code.trim(), typed_name: typedName.trim()
      })
      // Clear the persisted token so a shoulder-surfer + refresh can't retry.
      try { sessionStorage.removeItem(tokenStorageKey) } catch {}
      setStage('done')
    } catch (err) {
      setError(err.response?.data?.error || 'Sign failed.')
    } finally { setSaving(false) }
  }

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {children}
      </div>
    </div>
  )

  if (stage === 'loading') return <Shell><div className="p-8 text-center text-gray-500">Loading engagement letter…</div></Shell>

  if (stage === 'error') return (
    <Shell>
      <div className="p-8 text-center">
        <div className="text-5xl mb-3">⚠️</div>
        <h1 className="text-xl font-bold mb-2">Link problem</h1>
        <p className="text-gray-600">{error}</p>
        <p className="text-gray-500 text-sm mt-4">If you received this link from the firm, please open the exact URL from the email.  Contact the firm if the problem persists.</p>
      </div>
    </Shell>
  )

  if (stage === 'gone') return (
    <Shell>
      <div className="p-8 text-center">
        <div className="text-5xl mb-3">📄</div>
        <h1 className="text-xl font-bold mb-2">This letter is no longer available</h1>
        {status?.is_expired && <p className="text-gray-600">The link has expired.</p>}
        {status?.is_voided && <p className="text-gray-600">The letter was voided by the firm.</p>}
        <p className="text-gray-500 text-sm mt-4">Please contact the firm if you need a fresh link.</p>
      </div>
    </Shell>
  )

  if (stage === 'done') return (
    <Shell>
      <div className="p-8 text-center">
        <div className="text-5xl mb-3">✅</div>
        <h1 className="text-xl font-bold mb-2">Signed</h1>
        <p className="text-gray-600">Thank you.  The firm has been notified and will follow up shortly.</p>
      </div>
    </Shell>
  )

  if (stage === 'not_verified') return (
    <Shell>
      <div className="p-8 text-center">
        <div className="text-5xl mb-3">🔗</div>
        <h1 className="text-xl font-bold mb-2">Missing verification</h1>
        <p className="text-gray-600">{error}</p>
      </div>
    </Shell>
  )

  return (
    <Shell>
      <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
        <h1 className="text-lg font-bold">Engagement Letter</h1>
        <p className="text-xs text-gray-500 mt-0.5">Please review carefully.  If everything looks right, request a verification code and sign below.</p>
      </div>

      <div className="p-6 prose prose-sm max-w-none border-b border-gray-200 overflow-x-auto break-words" data-testid="engagement-letter-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />

      <form onSubmit={sign} className="p-6 space-y-4 bg-gray-50">
        <h2 className="text-base font-bold">Two-step signature</h2>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-sm min-w-0 grow basis-48">
              <div className="font-medium">Step 1 — Verification code</div>
              <div className="text-xs text-gray-500">We'll email a 6-digit code to <strong>{sentMasked}</strong>.  Enter it below.</div>
            </div>
            <button type="button" onClick={requestCode}
              disabled={cooldownSecondsLeft > 0}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50">
              {cooldownSecondsLeft > 0
                ? `📧 Resend in ${cooldownSecondsLeft}s`
                : codeSent ? '📧 Resend code' : '📧 Email me a code'}
            </button>
          </div>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required maxLength={6} pattern="^\d{6}$" inputMode="numeric" autoComplete="one-time-code"
            placeholder="6-digit code"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono tracking-widest" />
        </div>

        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-sm mb-2">
            <div className="font-medium">Step 2 — Type your full legal name</div>
            <div className="text-xs text-gray-500">Typing your name is your electronic signature.  It is legally binding under the ESIGN Act.</div>
          </div>
          <input value={typedName} onChange={e => setTypedName(e.target.value)}
            required maxLength={120} placeholder="Your full legal name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>

        <button type="submit" disabled={saving || !code || !typedName || code.length !== 6}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 rounded-lg disabled:opacity-50">
          {saving ? 'Signing…' : '✍️ Sign engagement letter'}
        </button>
        <p className="text-xs text-gray-500 text-center">
          By signing, you agree to the terms above.  A confirmation will be sent to your email.
        </p>
      </form>
    </Shell>
  )
}
